import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';

const SquareCheckoutForm = ({ amount, applicationId, locationId, onSuccess, onCancel }) => {
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [card, setCard] = useState(null);
  const cardContainerRef = useRef(null);
  const isInitializingRef = useRef(false);
  const cardInstanceRef = useRef(null);

  useEffect(() => {
    const initializeSquare = async () => {
      // Prevent multiple initializations
      if (isInitializingRef.current || cardInstanceRef.current) {
        return;
      }
      
      if (!window.Square) {
        // Load Square SDK
        const script = document.createElement('script');
        script.src = 'https://sandbox.web.squarecdn.com/v1/square.js';
        script.async = true;
        script.onload = () => initializeCard();
        document.body.appendChild(script);
      } else {
        initializeCard();
      }
    };

    const initializeCard = async () => {
      if (isInitializingRef.current || cardInstanceRef.current || !cardContainerRef.current) {
        return;
      }
      
      try {
        isInitializingRef.current = true;
        
        // Clear any existing content in the container
        if (cardContainerRef.current) {
          cardContainerRef.current.innerHTML = '';
        }
        
        const payments = window.Square.payments(applicationId, locationId);
        const cardInstance = await payments.card();
        await cardInstance.attach(cardContainerRef.current);
        
        cardInstanceRef.current = cardInstance;
        setCard(cardInstance);
        isInitializingRef.current = false;
      } catch (e) {
        console.error('Square initialization error:', e);
        setError('Failed to initialize Square payment form');
        toast.error('Failed to load payment form');
        isInitializingRef.current = false;
      }
    };

    initializeSquare();

    return () => {
      if (cardInstanceRef.current) {
        try {
          cardInstanceRef.current.destroy();
          cardInstanceRef.current = null;
        } catch (e) {
          console.error('Error destroying card:', e);
        }
      }
      isInitializingRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId, locationId]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!card) {
      setError('Payment form not ready');
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      // Tokenize card
      const result = await card.tokenize();

      if (result.status === 'OK') {
        const token = result.token;
        
        // Call backend to process payment
        const response = await fetch(`${process.env.REACT_APP_API_URL || '/api'}/payments/create-square-payment`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${sessionStorage.getItem('token')}`
          },
          body: JSON.stringify({
            sourceId: token,
            amount: amount
          })
        });

        const data = await response.json();

        if (data.success) {
          toast.success('Payment successful!');
          onSuccess({
            transactionId: data.paymentId,
            amount: amount
          });
        } else {
          setError(data.error || 'Payment failed');
          toast.error(data.error || 'Payment failed');
          setProcessing(false);
        }
      } else {
        const errorMessage = result.errors?.map(e => e.message).join(', ') || 'Card validation failed';
        setError(errorMessage);
        toast.error(errorMessage);
        setProcessing(false);
      }
    } catch (err) {
      console.error('Payment error:', err);
      setError(err.message || 'An error occurred');
      toast.error(err.message || 'Payment failed');
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="p-4 bg-gray-50 rounded-lg">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Card Details
        </label>
        <div 
          ref={cardContainerRef}
          id="card-container"
          className="p-3 bg-white border border-gray-300 rounded-md min-h-[60px]"
        />
        {error && (
          <p className="mt-2 text-sm text-red-600">{error}</p>
        )}
      </div>

      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex justify-between items-center">
          <span className="text-sm font-medium text-gray-700">Total Amount:</span>
          <span className="text-2xl font-bold text-blue-600">${amount}</span>
        </div>
      </div>


      <div className="flex space-x-3">
        <button
          type="button"
          onClick={onCancel}
          disabled={processing}
          className="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!card || processing}
          className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
        >
          {processing ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
              Processing...
            </>
          ) : (
            `Pay $${amount}`
          )}
        </button>
      </div>
    </form>
  );
};

export default SquareCheckoutForm;
